import { Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';

import type { MarketplaceProperty } from '../types';
import { PDP_HISTORY_DAY_CURRENT_URL, PDP_HISTORY_DAY_URL } from './media';

const historyLabels = {
  COMING_SOON: 'Coming soon',
  FOR_SALE: 'Listed for sale',
  OFF_MARKET: 'Taken off market',
  SALE_PENDING: 'Sale pending',
  SOLD: 'Sold',
  UNKNOWN: 'Listing updated',
  WITHDRAWN: 'Listing withdrawn',
} satisfies Record<MarketplaceProperty['listingHistory'][number]['state'], string>;

export const PdpPriceHistory = ({
  listingHistory,
}: Pick<MarketplaceProperty, 'listingHistory'>) => {
  const events = listingHistory.flatMap((event) => {
    if (!event.timestamp || (event.state === 'SALE_PENDING' && !event.mls)) return [];
    const date = new Date(event.timestamp);
    if (!Number.isFinite(date.getTime())) return [];
    return [{ ...event, date }];
  });

  return (
    <Col as="section" aria-label="Price history" className="gap-10">
      <Text as="h2" className="text-label-lg">
        Price History
      </Text>
      {events.length === 0 ? (
        <Text className="text-body-md text-od-content-secondary">No price history</Text>
      ) : (
        /* Rows are separated by a 1px #EAE8E0 rule with 16px above and below. */
        <Col role="list" className="divide-y divide-[#EAE8E0]">
          {events.map((event, index) => {
            const offMarket = event.state === 'OFF_MARKET' || event.state === 'WITHDRAWN';
            const priceCents =
              event.state === 'SOLD'
                ? event.soldPriceCents
                : (event.currentListPriceCents ?? event.originalListPriceCents);
            const hasPrice = !offMarket && priceCents != null;
            const statusLabel = historyLabels[event.state];
            return (
              <Row
                role="listitem"
                key={`${event.timestamp}-${event.state}-${index}`}
                className="items-center gap-x-8 py-4 first:pt-0 last:pb-0"
              >
                <time
                  dateTime={event.date.toISOString()}
                  aria-label={event.date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    timeZone: 'UTC',
                  })}
                  // Fixed width (28px marker + 8px + room for the widest month/year) keeps every price on one guideline.
                  className="flex w-[68px] shrink-0 items-center gap-2 text-od-content-primary"
                >
                  <Text as="span" className="relative flex size-7 items-center justify-center">
                    <Image
                      src={index === 0 ? PDP_HISTORY_DAY_CURRENT_URL : PDP_HISTORY_DAY_URL}
                      width={28}
                      height={28}
                      alt=""
                      aria-hidden
                      className="absolute inset-0 size-7"
                    />
                    <Text
                      as="span"
                      className={cn(
                        'relative text-[14px] leading-none',
                        index === 0 ? 'text-blue-950' : 'text-od-content-secondary',
                      )}
                    >
                      {event.date.getUTCDate()}
                    </Text>
                  </Text>
                  <Col className="gap-0.5 text-body-xs leading-none">
                    <Text as="span" className="text-body-xs leading-none">
                      {event.date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}
                    </Text>
                    <Text as="span" className="text-body-xs leading-none">
                      {event.date.getUTCFullYear()}
                    </Text>
                  </Col>
                </time>
                <Text className="shrink-0 text-[18px] leading-[1.4] text-[#231E1B]">
                  {hasPrice ? formatCurrency(priceCents / 100) : statusLabel}
                </Text>
                {(hasPrice || event.mls) && (
                  <Text className="flex-1 text-right whitespace-pre-wrap text-body-sm text-[#514C48]">
                    {hasPrice
                      ? `${statusLabel}${event.mls ? `   •   ${event.mls}` : ''}`
                      : event.mls}
                  </Text>
                )}
              </Row>
            );
          })}
        </Col>
      )}
    </Col>
  );
};
